import React               from 'react';
import styled              from 'styled-components';
import {
  Form,
  Field,
  FormikValues,
  FormikErrors,
  FormikTouched,
}                          from 'formik';
import moment              from 'moment';
import { Grid }            from '@material-ui/core';
import { Breakpoint }      from '@material-ui/core/styles/createBreakpoints';
import {
  ILocation,
  ICarfaxReport,
  IInventoryContact,
}                          from '@typings/auction';
import { ISelectOption }   from '@typings/components';
import {
  bodyStyleOptions,
  exteriorConditionOptions,
  interiorConditionOptions,
  mechanicalConditionOptions,
}                          from '@modules/auctions/utils/suggestions';
import {
  Dropdown,
  Switch,
  TextInputGray,
  RequiredSign,
  VinWrapper,
  Button,
}                          from '@components/index';
import { DoubleButton }    from '@modules/accountManagement/components/DoubleButton';
import InfoImage           from '../assets/undraw_create.svg';
import UndrawCheckingBoxes from '../assets/undraw_checking_boxes.svg';
import UndrawVehicleSale   from '../assets/undraw_vehicle_sale.svg';

interface IProps {
  width             : Breakpoint;
  dirty             : boolean;
  values            : FormikValues;
  touched           : FormikTouched<any>;
  errors            : FormikErrors<any>;
  isValid           : boolean;
  isSubmitting      : boolean;
  locations         : ILocation[];
  inventoryContacts : IInventoryContact[];
  onInputVinCode    : (event: React.ChangeEvent<HTMLInputElement>) => void;
  isLoadingUpdate   : boolean;
  carfaxReport?     : ICarfaxReport;
}

const Title = styled.p`
  text-align  : ${({ theme: { width } }): string => (width !== 'xs' ? 'right' : 'left')};
  margin-top  : 2px;
  font-size   : 20px;
  font-weight : 300;
  white-space : ${({ theme: { width } }): string => (width !== 'xs' && width !== 'sm' ? 'nowrap' : 'unset')};
  float       : ${({ theme: { width } }): string => (width === 'xs' ? 'left' : 'right')};
  min-width   : 150px;
` as any;

export const MainTitle = styled.h2`
  display     : flex;
  align-items : center;
  font-weight : 400;
  margin      : 16px 0 0 0;
  font-size   : ${({ theme: { width } }): number => (width === 'xs' ? 20 : 28)}px;
`;

export const Description = styled.p`
  margin-top  : 4px;
  font-weight : 300;
  font-size   : 14px;

  p {
    margin: 10px 0;
  }
`;

const InfoImageWrapper = styled.div`
  position : absolute;
  top      : 90px;
  left     : 60%;
`;

const UndrawCheckingBoxesWrapper = styled.div`
  position : relative;
  width    : 100%;

  > div {
    position : absolute;
    bottom   : -20px;
    left     : 65%;
  }
`;

const UndrawVehicleSaleWrapper = styled.div`
  position : relative;
  width    : 100%;

  > div {
    position : absolute;
    bottom   : 20px;
    right    : 50px;
  }
`;

const FieldWithInfoWrapper = styled.div`
  position: relative;
`;

const Spacer = styled.div`
  width  : 100%;
  height : ${({ theme: { width } }): number => (width !== 'xs' ? 80 : 30)}px;
`;

const ButtonsWrapper = styled.div`
  display         : flex;
  justify-content : flex-end;
  margin-left     : auto;
  padding-top     : 20px;

  ${({ theme: { width } }): false | string => width === 'xs' && `
    flex-direction : column;
    height         : 104px;
  `}
`;

const CarfaxDescription = styled(Title)`&& {
  text-align: left;
  float: unset;

  &>span:last-of-type {
    font-weight: 400;
  }
}`;

export const AuctionDetailsForm: React.FC<IProps> = ({
  width,
  dirty,
  values,
  touched,
  errors,
  isValid,
  isSubmitting,
  inventoryContacts,
  locations,
  onInputVinCode,
  isLoadingUpdate,
  carfaxReport,
}: IProps): JSX.Element => {
  const isXs = width === 'xs';
  const isSm = width === 'sm';

  const locationsValues = locations.map(
    (location: ILocation): ISelectOption => ({
      label : location.locationName,
      value : location.id,
    }),
  );

  const inventoryValues = inventoryContacts.map(
    (inventory: IInventoryContact): ISelectOption => ({
      label : inventory.contactName,
      value : inventory.id,
    }),
  );

  const locationOptions = [
    { label: null, options: locationsValues },
    { label: 'Add Location', withAction: true, onClick: (): any => null },
  ];

  const inventoriesOptions = [
    { label: null, options: inventoryValues },
    { label: 'Add Inventory Contact', withAction: true, onClick: (): any => null },
  ];

  return (
    <Form>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Grid container spacing={isXs ? 1 : 3}>
            {/* Title */}
            {!isXs && <Grid item sm={2} md={2} />}

            <Grid item xs={12} sm={7} md={5}>
              <MainTitle>Vehicle Information</MainTitle>
            </Grid>

            {!isXs && <Grid item sm={3} md={5} />}
            {/* Title */}

            {/* VIN */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                VIN:
              </Title>
            </Grid>

            <Grid item xs={12} sm={9} md={5}>
              <VinWrapper>
                <Field
                  id          = "vin"
                  name        = "vin"
                  type        = "text"
                  placeholder = "VIN"
                  error       = {touched.vin && !!errors.vin}
                  component   = {TextInputGray}
                  onInput     = {onInputVinCode}
                  inputProps  = {{ maxLength: 20 }}
                  stripChars  = "[^a-zA-Z0-9]"
                />
              </VinWrapper>
            </Grid>

            {!isXs && <Grid item sm={1} md={5} />}
            {/* VIN */}

            {/* Body Style */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                Body Style:
              </Title>
            </Grid>

            <Grid item xs={12} sm={5} md={2}>
              <Field
                id          = "vehicleBodyStyle"
                name        = "vehicleBodyStyle"
                component   = {Dropdown}
                options     = {bodyStyleOptions}
                error       = {touched.vehicleBodyStyle && !!errors.vehicleBodyStyle}
                placeholder = "Body Style"
              />
            </Grid>

            {!isXs && <Grid item sm={5} md={8} />}
            {/* Body Style */}

            {/* Year */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                Year:
              </Title>
            </Grid>

            <Grid item xs={12} sm={5} md={2}>
              <Field
                id            = "year"
                name          = "year"
                type          = "number"
                error         = {touched.year && !!errors.year}
                placeholder   = "Year"
                component     = {TextInputGray}
                inputProps    = {{ maxLength: 4 }}
                allowNegative = {false}
              />
            </Grid>

            {!isXs && <Grid item sm={5} md={8} />}
            {/* Year */}

            {/* Make */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                Make:
              </Title>
            </Grid>

            <Grid item xs={12} sm={9} md={5}>
              <Field
                id          = "carBrandName"
                name        = "carBrandName"
                type        = "text"
                error       = {touched.carBrandName && !!errors.carBrandName}
                placeholder = "Make"
                component   = {TextInputGray}
                inputProps  = {{ maxLength: 35 }}
              />
            </Grid>

            {!isXs && <Grid item xs={3} sm={1} md={5} />}
            {/* Make */}

            {/* Model */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                Model:
              </Title>
            </Grid>

            <Grid item xs={12} sm={9} md={5}>
              <Field
                id          = "carModelName"
                name        = "carModelName"
                type        = "text"
                error       = {touched.carModelName && !!errors.carModelName}
                placeholder = "Model"
                component   = {TextInputGray}
                inputProps  = {{ maxLength: 35 }}
              />
            </Grid>

            {!isXs && <Grid item xs={3} sm={1} md={5} />}
            {/* Model */}

            {/* Current Miles */}
            <Grid item xs={12} sm={2} md={2}>
              <Title>
                <RequiredSign />
                Current Miles:
              </Title>
            </Grid>

            <Grid item xs={12} sm={5} md={2}>
              <Field
                id               = "miles"
                name             = "miles"
                type             = "number"
                error            = {touched.miles && !!errors.miles}
                placeholder      = "Current Miles"
                component        = {TextInputGray}
                inputProps       = {{ maxLength: 10 }}
                allowNegative    = {false}
                decimalSeparator = {false}
                thousandSeparator
              />
            </Grid>

            {!isXs && <Grid item sm={5} md={8} />}
            {/* Current Miles */}
          </Grid>
        </Grid>

        {(!isXs && !isSm) && (
          <InfoImageWrapper>
            <InfoImage viewBox="0 0 390 330" width="340" height="290" />
          </InfoImageWrapper>
        )}
      </Grid>

      <Grid container spacing={3}>
        {/* Vehicle Description */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>Vehicle Description:</Title>
        </Grid>

        <Grid item xs={12} sm={9} md={9}>
          <FieldWithInfoWrapper>
            <Field
              id          = "vehicleDescription"
              name        = "vehicleDescription"
              error       = {touched.vehicleDescription && !!errors.vehicleDescription}
              placeholder = "This vehicle is a great buy because..."
              component   = {TextInputGray}
              multiline
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
        {/* Vehicle Description */}

        {/* Features and Equipment */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Features and Equipment:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={9}>
          <FieldWithInfoWrapper>
            <Field
              id          = "featuresAndEquipment"
              name        = "featuresAndEquipment"
              error       = {touched.featuresAndEquipment && !!errors.featuresAndEquipment}
              placeholder = "3.8L, V6, OHV, 4WD, Seats 5, Anti Theft..."
              component   = {TextInputGray}
              multiline
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
        {/* Features and Equipment */}
      </Grid>

      {carfaxReport && (
        <>
          <Spacer />

          <Grid container spacing={3}>
            {/* Title */}
            {!isXs && <Grid item sm={2} md={2} />}

            <Grid item xs={12} sm={10} md={10}>
              <MainTitle>CARFAX - Vehicle History Report</MainTitle>
            </Grid>
            {/* Title */}

            {/* Carfax Info */}
            {!isXs && <Grid item xs={12} sm={2} md={2} />}

            <Grid item xs={12} sm={10} md={9}>
              <CarfaxDescription>
                <span>CARFAX - Vehicle History Report:</span>
                &nbsp;
                <span>{`Attached ${moment(carfaxReport.dateCreated).format('MM/DD/YYYY hh:mm a')}`}</span>
              </CarfaxDescription>
            </Grid>
            {/* Carfax Info */}
          </Grid>
        </>
      )}

      <Spacer />

      <Grid container spacing={3}>
        {/* Title */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={10}>
          <MainTitle>Condition</MainTitle>
        </Grid>
        {/* Title */}

        {/* Full Disclosure */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Full Disclosure:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={9}>
          <FieldWithInfoWrapper>
            <Field
              id          = "fullDisclosure"
              name        = "fullDisclosure"
              error       = {touched.fullDisclosure && !!errors.fullDisclosure}
              placeholder = "One inch scratch on the passenger front door..."
              component   = {TextInputGray}
              multiline
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
        {/* Full Disclosure */}

        {/* Exterior Condition */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Exterior Condition:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={6}>
          <Field
            id           = "exteriorCondition"
            name         = "exteriorCondition"
            error        = {touched.exteriorCondition && !!errors.exteriorCondition}
            defaultValue = {2}
            options      = {exteriorConditionOptions}
            component    = {Dropdown}
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={4} />}
        {/* Exterior Condition */}

        {/* Interior Condition */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Interior Condition:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={6}>
          <Field
            id           = "interiorCondition"
            name         = "interiorCondition"
            error        = {touched.interiorCondition && !!errors.interiorCondition}
            defaultValue = {2}
            options      = {interiorConditionOptions}
            component    = {Dropdown}
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={4} />}
        {/* Interior Condition */}

        {/* Mechanical Condition */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Mechanical Condition:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={6}>
          <Field
            id           = "mechanicalCondition"
            name         = "mechanicalCondition"
            error        = {touched.mechanicalCondition && !!errors.mechanicalCondition}
            defaultValue = {2}
            options      = {mechanicalConditionOptions}
            component    = {Dropdown}
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={4} />}
        {/* Mechanical Condition */}

        {(!isXs && !isSm) && (
          <UndrawCheckingBoxesWrapper>
            <div>
              <UndrawCheckingBoxes viewBox="0 0 300 210" width="300" height="170" />
            </div>
          </UndrawCheckingBoxesWrapper>
        )}
      </Grid>

      <Spacer />

      <Grid container spacing={3}>
        {/* Title */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={10}>
          <MainTitle>Vehicle Location</MainTitle>
        </Grid>
        {/* Title */}

        {/* Location */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={9} md={9}>
          <Field
            id              = "locationId"
            name            = "locationId"
            error           = {touched.locationId && !!errors.locationId}
            options         = {locationOptions}
            backgroundColor = "#4091F7"
            component       = {Dropdown}
            disabled
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
        {/* Location */}
      </Grid>

      <Grid container spacing={3}>
        {/* Title */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={10}>
          <MainTitle>Inventory Contact</MainTitle>
        </Grid>
        {/* Title */}

        {/* Inventory Contact */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={9} md={9}>
          <Field
            id              = "inventoryContactId"
            name            = "inventoryContactId"
            error           = {touched.inventoryContactId && !!errors.inventoryContactId}
            options         = {inventoriesOptions}
            backgroundColor = "#4091F7"
            component       = {Dropdown}
            disabled
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
        {/* Location */}
      </Grid>

      <Spacer />

      <Grid container spacing={3}>
        {/* Title */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={10}>
          <MainTitle>Pricing</MainTitle>
        </Grid>
        {/* Title */}

        {/* Minimum Bid */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Minimum Bid:
          </Title>
        </Grid>

        <Grid item xs={12} sm={5} md={2}>
          <FieldWithInfoWrapper>
            <Field
              id            = "minimumBid"
              name          = "minimumBid"
              placeholder   = "Minimum Bid"
              type          = "number"
              error         = {touched.minimumBid && !!errors.minimumBid}
              label         = "$"
              component     = {TextInputGray}
              inputProps    = {{ maxLength: 10 }}
              decimalScale  = {2}
              allowNegative = {false}
              thousandSeparator
              disabled
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={5} md={8} />}
        {/* Minimum Bid */}

        {/* Bid Increment */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Bid Increment:
          </Title>
        </Grid>

        <Grid item xs={12} sm={5} md={2}>
          <FieldWithInfoWrapper>
            <Field
              id            = "bidIncrement"
              name          = "bidIncrement"
              placeholder   = "Bid Increment"
              type          = "number"
              error         = {touched.bidIncrement && !!errors.bidIncrement}
              label         = "$"
              component     = {TextInputGray}
              decimalScale  = {2}
              inputProps    = {{ maxLength: 10 }}
              allowNegative = {false}
              thousandSeparator
              disabled
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={5} md={8} />}
        {/* Bid Increment */}

        {/* Retail Value */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Retail Value:
          </Title>
        </Grid>

        <Grid item xs={12} sm={5} md={2}>
          <FieldWithInfoWrapper>
            <Field
              id            = "retailValue"
              name          = "retailValue"
              placeholder   = "Retail Value"
              type          = "number"
              error         = {touched.retailValue && !!errors.retailValue}
              label         = "$"
              decimalScale  = {2}
              component     = {TextInputGray}
              inputProps    = {{ maxLength: 10 }}
              allowNegative = {false}
              thousandSeparator
              disabled
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={5} md={8} />}
        {/* Retail Value */}

        {/* Wholesale Value */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>Wholesale Value:</Title>
        </Grid>

        <Grid item xs={12} sm={5} md={2}>
          <FieldWithInfoWrapper>
            <Field
              id            = "wholesaleValue"
              name          = "wholesaleValue"
              placeholder   = "Wholesale Value"
              type          = "number"
              error         = {touched.wholesaleValue && !!errors.wholesaleValue}
              label         = "$"
              decimalScale  = {2}
              component     = {TextInputGray}
              inputProps    = {{ maxLength: 10 }}
              allowNegative = {false}
              thousandSeparator
              disabled
            />
          </FieldWithInfoWrapper>
        </Grid>

        {!isXs && <Grid item sm={5} md={8} />}
        {/* Wholesale Value */}

        {(!isXs && !isSm) && (
          <UndrawVehicleSaleWrapper>
            <div>
              <UndrawVehicleSale viewBox="0 0 500 210" width="460" height="220" />
            </div>
          </UndrawVehicleSaleWrapper>
        )}

        {/* Payment Terms */}
        <Grid item xs={12} sm={2} md={2}>
          <Title>
            <RequiredSign />
            Payment Terms:
          </Title>
        </Grid>

        <Grid item xs={12} sm={9} md={9}>
          <Field
            id        = "paymentTerms"
            name      = "paymentTerms"
            component = {TextInputGray}
            error     = {touched.paymentTerms && !!errors.paymentTerms}
            multiline
            disabled
          />
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}

        {/* Switch Button */}
        {!isXs && <Grid item xs={12} sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={9}>
          <Field
            id        = "includeSpecialFinancingOffer"
            name      = "includeSpecialFinancingOffer"
            label     = "Include Special Financing Offer"
            component = {Switch}
            disabled
          />
        </Grid>
        {/* Switch Button */}

        {values.includeSpecialFinancingOffer && (
          <>
            {!isXs && <Grid item xs={12} sm={2} md={2} />}

            <Grid item xs={12} sm={9} md={9}>
              <Field
                id        = "specialFinancingOffer"
                name      = "specialFinancingOffer"
                component = {TextInputGray}
                multiline
                disabled
              />
            </Grid>

            {!isXs && <Grid item sm={1} md={1} />}
          </>
        )}
        {/* Payment Terms */}
      </Grid>

      <Spacer />

      <Grid container spacing={3}>
        {/* Title */}
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={10} md={10}>
          <MainTitle>Audience</MainTitle>
        </Grid>
        {/* Title */}

        <Grid item xs={12} sm={2} md={2} />

        <Grid item xs={12} sm={9} md={9}>
          <DoubleButton name="audience" disabled />
        </Grid>

        {!isXs && <Grid item sm={1} md={1} />}
      </Grid>

      <Grid container spacing={3}>
        {!isXs && <Grid item sm={2} md={2} />}

        <Grid item xs={12} sm={9} md={9}>
          <ButtonsWrapper>
            <Button
              type      = "submit"
              label     = "Save"
              isLoading = {isLoadingUpdate}
              disabled  = {!isValid || !dirty || isLoadingUpdate || isSubmitting}
            />
          </ButtonsWrapper>
        </Grid>
      </Grid>
    </Form>
  );
};
